import { useState, useEffect, useCallback } from 'react';

const COOKIE_CONSENT_KEY = 'neeli_cookie_consent';

export interface CookiePreferences {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  consentedAt: string;
}

const DEFAULT_PREFERENCES: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false,
  consentedAt: '',
};

export function useCookieConsent() {
  const [preferences, setPreferencesState] = useState<CookiePreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        setPreferencesState(JSON.parse(stored));
      } catch {
        setPreferencesState(null);
      }
    }
    setIsLoading(false);
  }, []);

  const hasConsented = preferences !== null && preferences.consentedAt !== '';

  const savePreferences = useCallback((prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferencesState(prefs);
  }, []);

  const acceptAll = useCallback(() => {
    const prefs: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      consentedAt: new Date().toISOString(),
    };
    savePreferences(prefs);
  }, [savePreferences]);

  const rejectAll = useCallback(() => {
    const prefs: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      consentedAt: new Date().toISOString(),
    };
    savePreferences(prefs);
  }, [savePreferences]);

  const setPreferences = useCallback((prefs: Partial<Omit<CookiePreferences, 'essential'>>) => {
    const newPrefs: CookiePreferences = {
      essential: true,
      analytics: prefs.analytics ?? preferences?.analytics ?? false,
      marketing: prefs.marketing ?? preferences?.marketing ?? false,
      consentedAt: new Date().toISOString(),
    };
    savePreferences(newPrefs);
  }, [preferences, savePreferences]);

  return {
    hasConsented,
    preferences,
    isLoading,
    acceptAll,
    rejectAll,
    setPreferences,
  };
}
